/**
 * Mobile search inline-dropdown behavior.
 *
 * The always-visible header search bar (`.header-mobile__search`) shows a
 * dropdown panel below itself when the user interacts — preserving the
 * surrounding page (logo, header, body) context. There is NO fullscreen
 * overlay, NO body scroll lock, NO history-state manipulation. The bar
 * stays put; only the dropdown comes and goes.
 *
 * States:
 *   .is-search-active   — input has focus or value; magnifier hides, X shows
 *   .has-query          — input has a non-empty value; trending dropdown hides
 *                         (predictive-search.js takes over showing live results)
 *
 * The X button (`.search__cancel`) clears the input, blurs (auto-dismisses
 * the soft keyboard), and removes both state classes.
 *
 * Panel height: the panel runs from just below the bar to just above the
 * bottom of the *visual* viewport. iOS does not resize the layout viewport
 * for the soft keyboard (so no vh/dvh unit tracks it); visualViewport does.
 * --mobile-search-panel-max is re-measured while the panel is open.
 */
(function () {
  let initialized = false;

  function init() {
    if (initialized) return;
    const root = document.querySelector('.header-mobile__search');
    if (!root) return;
    const form = root.querySelector('.search-mobile__form');
    const input = form && form.querySelector('input[type="search"]');
    const cancelBtn = form && form.querySelector('[data-search-cancel]');
    if (!form || !input || !cancelBtn) return;
    initialized = true;

    function syncState() {
      const hasValue = !!(input.value && input.value.length > 0);
      const focused = document.activeElement === input;
      root.classList.toggle('is-search-active', focused || hasValue);
      root.classList.toggle('has-query', hasValue);
      fitPanel();
    }

    let fitFrame = 0;
    function fitPanel() {
      if (fitFrame || !root.classList.contains('is-search-active')) return;
      fitFrame = requestAnimationFrame(function () {
        fitFrame = 0;
        const vv = window.visualViewport;
        const viewBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
        const edge = parseFloat(getComputedStyle(root).getPropertyValue('--screen-edge-mb')) || 1;
        // One edge gap above the panel (below the bar) and one below it.
        const max = viewBottom - root.getBoundingClientRect().bottom - edge * 2;
        root.style.setProperty('--mobile-search-panel-max', Math.max(max, 160) + 'px');
      });
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', fitPanel);
      window.visualViewport.addEventListener('scroll', fitPanel);
    }
    window.addEventListener('scroll', fitPanel, { passive: true });
    window.addEventListener('resize', fitPanel);

    input.addEventListener('focus', syncState);
    input.addEventListener('input', syncState);

    // No blur listener: blur fires whenever the user taps anything inside
    // the dropdown (chip ×, suggestion pill, popular product), which would
    // otherwise tear the panel down before the click handlers run. Tap-
    // outside is handled by the document click listener below; explicit
    // dismiss paths (X button, Escape, Clear all) all flow through
    // closeSearch().

    function closeSearch() {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.blur();
      root.classList.remove('is-search-active', 'has-query');
    }

    // X button: clear + blur + reset state.
    cancelBtn.addEventListener('click', function (e) {
      e.preventDefault();
      closeSearch();
    });

    // "Clear all" on the recent-searches header also dismisses the dropdown
    // entirely (per user request). predictive-search.js's own delegated
    // listener is attached to the predictive-search element and runs on the
    // same click event — JavaScript dispatches listeners in attachment
    // order on a single target, but for delegated listeners on different
    // ancestors the inner (predictive-search element) fires before this
    // outer (.header-mobile__search). setTimeout(0) defers closeSearch to
    // the next tick — strictly after predictive-search.js has cleared
    // localStorage — so the closed bar shows the empty state on next open.
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-recent-clear-all]')) {
        setTimeout(closeSearch, 0);
      }
    });

    // Tap-outside dismisses (predictive-search.js already closes its own
    // result panel; this handles the bar state and trending-block class).
    document.addEventListener('click', function (e) {
      if (!root.classList.contains('is-search-active')) return;
      if (root.contains(e.target)) return;
      root.classList.remove('is-search-active', 'has-query');
    });

    // Escape clears + closes (desktop a11y parity, harmless on mobile).
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!root.classList.contains('is-search-active')) return;
      closeSearch();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
